import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { Scale } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

export default function BreakEvenRentCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [mortgage, setMortgage] = useState("")
  const [tax, setTax] = useState("")
  const [insurance, setInsurance] = useState("")
  const [hoa, setHoa] = useState("")
  const [maintenance, setMaintenance] = useState("")
  const [managementFee, setManagementFee] = useState("")
  const [vacancyRate, setVacancyRate] = useState("")
  const [targetProfit, setTargetProfit] = useState("")

  const results = useMemo(() => {
    const m = parseFloat(mortgage) || 0
    const t = parseFloat(tax) || 0
    const ins = parseFloat(insurance) || 0
    const h = parseFloat(hoa) || 0
    const maint = parseFloat(maintenance) || 0
    const mgmt = parseFloat(managementFee) || 0
    const vac = parseFloat(vacancyRate) || 0
    const tp = parseFloat(targetProfit) || 0

    const fixedCosts = m + t + ins + h + maint
    if (fixedCosts === 0) return null

    const variableRate = (mgmt + vac) / 100
    const breakEvenRent = fixedCosts / (1 - variableRate)
    const targetRent = (fixedCosts + tp) / (1 - variableRate)
    const surplus = targetRent - breakEvenRent

    const breakdown = [
      { label: "Mortgage", value: m },
      { label: "Property Tax", value: t },
      { label: "Insurance", value: ins },
      { label: "HOA / Service Charge", value: h },
      { label: "Maintenance Reserve", value: maint },
    ].filter(x => x.value > 0)

    return { fixedCosts, variableRate, breakEvenRent, targetRent, surplus, breakdown }
  }, [mortgage, tax, insurance, hoa, maintenance, managementFee, vacancyRate, targetProfit])

  const fmt = (n: number) => `${currency}${n.toFixed(2)}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Monthly Costs</p>

              {([
                ["Mortgage Payment", mortgage, setMortgage, "e.g. 800"],
                ["Property Tax (monthly)", tax, setTax, "e.g. 150"],
                ["Insurance (monthly)", insurance, setInsurance, "e.g. 50"],
                ["HOA / Service Charge", hoa, setHoa, "e.g. 80"],
                ["Maintenance Reserve", maintenance, setMaintenance, "e.g. 100"],
              ] as const).map(([label, val, setter, ph]) => (
                <div key={label as string}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{label as string}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                    <input
                      type="number"
                      min="0"
                      value={val as string}
                      onChange={e => (setter as (v: string) => void)(e.target.value)}
                      placeholder={ph as string}
                      className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              ))}

              <div className="border-t border-slate-200 pt-3 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Variable Costs</p>

                {([
                  ["Management Fee %", managementFee, setManagementFee, "e.g. 10"],
                  ["Vacancy Rate %", vacancyRate, setVacancyRate, "e.g. 8"],
                ] as const).map(([label, val, setter, ph]) => (
                  <div key={label as string}>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{label as string}</label>
                    <div className="relative">
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={val as string}
                        onChange={e => (setter as (v: string) => void)(e.target.value)}
                        placeholder={ph as string}
                        className="w-full rounded-lg border border-slate-200 bg-white pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                  </div>
                ))}

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Target Monthly Profit (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                    <input
                      type="number"
                      min="0"
                      value={targetProfit}
                      onChange={e => setTargetProfit(e.target.value)}
                      placeholder="e.g. 200"
                      className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter your monthly costs to calculate break-even rent</p>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Break-Even Rent</p>
                    <p className="text-5xl font-extrabold text-primary">{fmt(results.breakEvenRent)}</p>
                    <p className="text-sm text-slate-500 mt-2">Minimum rent to avoid losing money</p>
                  </div>

                  <div className="border-t border-primary/10 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Total Fixed Costs</span>
                      <span className="font-semibold text-slate-900">{fmt(results.fixedCosts)}/mo</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Variable Rate (mgmt + vacancy)</span>
                      <span className="font-semibold text-slate-900">{(results.variableRate * 100).toFixed(1)}%</span>
                    </div>
                    {parseFloat(targetProfit) > 0 && (
                      <div className="border-t border-primary/10 pt-3 flex justify-between text-sm">
                        <span className="text-slate-600">Target Rent (with profit)</span>
                        <span className="font-semibold text-primary">{fmt(results.targetRent)}</span>
                      </div>
                    )}
                  </div>

                  {/* Cost Breakdown Bars */}
                  {results.breakdown.length > 0 && (
                    <div className="border-t border-primary/10 pt-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Cost Breakdown</p>
                      <div className="space-y-2">
                        {results.breakdown.map(({ label, value }) => {
                          const pct = results.fixedCosts > 0 ? (value / results.fixedCosts) * 100 : 0
                          return (
                            <div key={label}>
                              <div className="flex justify-between text-xs text-slate-600 mb-0.5">
                                <span>{label}</span>
                                <span>{fmt(value)}</span>
                              </div>
                              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Fixed Costs = Mortgage + Tax + Insurance + HOA + Maintenance\nVariable Rate = (Management Fee % + Vacancy Rate %) ÷ 100\n\nBreak-Even Rent = Fixed Costs ÷ (1 − Variable Rate)\nTarget Rent = (Fixed Costs + Target Profit) ÷ (1 − Variable Rate)`}
        description="Dividing by (1 − variable rate) accounts for the fact that management fees and vacancy are expressed as a percentage of rent — so your gross rent must be higher to net the same fixed costs."
      />

      <ToolFAQ
        items={[
          {
            q: "Why is break-even rent higher than just my fixed costs?",
            a: "Because management fees and vacancy are calculated as a percentage of rent. To net enough to cover fixed costs after these deductions, you need to charge slightly more than the fixed costs alone. The formula accounts for this by dividing by (1 − variable rate).",
          },
          {
            q: "What is a reasonable maintenance reserve to budget?",
            a: "The rule of thumb is 1% of property value per year divided by 12 months. So for a {currency}250,000 property, budget around {currency}208/month. Older properties or those with older systems (boiler, roof) should budget closer to 1.5–2%.",
          },
          {
            q: "Should I include a vacancy allowance even if my property is currently occupied?",
            a: "Yes. No property stays occupied 100% of the time over the long run. Including a 5–8% vacancy allowance ensures your break-even calculation remains realistic over the life of the investment, not just for the current tenancy.",
          },
        ]}
      />

      <CTABox
        icon={Scale}
        heading="Know your break-even instantly"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
